/**
 * q-event-pickers.tsx — the EXACT booking-questions + surveys picker UX from
 * the Events wizard (CreateEventRegistration.tsx: EventBookingQuestions /
 * EventSurveys — operator direction 2026-07-21: "use the exact UX used for
 * booking questions and surveys in events"), extracted as CONTROLLED
 * components for the queue page builder. No store coupling — the caller
 * passes the questions/groups library and the surveys list as props.
 */
import { useEffect, useState } from "react";
import { MediaRow, IconBtn, Btn, Icon, Input, Pill, T } from "@v0y4ge/jrni-ui";

import { useT } from "@v0y4ge/i18n-runtime";
export interface QPickQuestion { id: string; label: string }
export interface QPickGroup { id: string; name: string; count: number }
export interface QPickSurvey { id: string; name: string; count: number; description?: string }
export type QPickRef = { kind: "question" | "group"; id: string };

// ── Generic picker modal chrome (verbatim from CreateEventRegistration) ──────
export function QPickerModal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; document.addEventListener("keydown", k); return () => document.removeEventListener("keydown", k); }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,18,25,.42)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(560px, 100%)", maxHeight: "80vh", background: T.white, borderRadius: 14, boxShadow: "0 24px 60px rgba(15,18,25,.28)", display: "flex", flexDirection: "column", overflow: "hidden", fontFamily: T.font }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{title}</div>
          <IconBtn size={32} onClick={onClose}><Icon.x size={18} /></IconBtn>
        </div>
        {children}
      </div>
    </div>
  );
}
const pickRowStyle = { display: "flex", alignItems: "center", gap: 12, padding: 11, border: `1px solid ${T.border}`, borderRadius: 10, background: T.white, cursor: "pointer", fontFamily: T.font, width: "100%", textAlign: "left" as const };

function PickHead({ title, hint }: { title: string; hint?: string }) {
  return (
    <div style={{ marginBottom: 2 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{title}</div>
      {hint ? <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.45, marginTop: 2 }}>{hint}</div> : null}
    </div>
  );
}

// ── Booking questions (library picker + drag-to-reorder — events UX) ─────────
export function QBookingQuestionsPicker({ items, questions, groups, onChange, title = "Booking questions", hint = "Questions and question groups asked on this page. Drag to reorder; they appear in this order." }:
  { items: QPickRef[]; questions: QPickQuestion[]; groups: QPickGroup[]; onChange: (next: QPickRef[]) => void; title?: string; hint?: string }) {
  const t = useT();
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);

  const resolve = (it: QPickRef) => it.kind === "group" ? (groups.find((g) => g.id === it.id) || {}) : (questions.find((q) => q.id === it.id) || {});
  const add = (kind: "question" | "group", id: string) => { if (!items.some((x) => x.kind === kind && x.id === id)) onChange([...items, { kind, id }]); };
  const rm = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const taken = new Set(items.map((x) => x.kind + x.id));
  const qs = questions.filter((q) => !taken.has("question" + q.id) && (!query.trim() || (q.label || "").toLowerCase().includes(query.toLowerCase())));
  const gs = groups.filter((g) => !taken.has("group" + g.id) && (!query.trim() || (g.name || "").toLowerCase().includes(query.toLowerCase())));

  const onDrop = (target: number) => {
    if (dragIdx === null || dragIdx === target) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(target, 0, moved);
    onChange(next); setDragIdx(null); setOverIdx(null);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <PickHead title={title} hint={hint} />
      {items.length === 0 ? (
        <div style={{ padding: 14, background: T.n6, borderRadius: 8, fontSize: 12.5, color: T.soft }}>{t('queues.qEventPickers.text')}</div>
      ) : items.map((it, i) => {
        const obj = resolve(it) as { name?: string; label?: string; count?: number };
        const isGroup = it.kind === "group";
        return (
          <div key={it.kind + it.id} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }} onDrop={() => onDrop(i)} onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", border: `1px solid ${overIdx === i && dragIdx !== null ? T.p1 : T.border}`, borderRadius: 9, background: dragIdx === i ? T.p4 : T.white, opacity: dragIdx === i ? 0.6 : 1, cursor: "grab" }}>
            <Icon.grip size={15} style={{ color: T.n3, flexShrink: 0 }} />
            <span style={{ width: 26, height: 26, borderRadius: 6, background: isGroup ? T.p4 : T.n6, color: isGroup ? T.p1 : T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{isGroup ? <Icon.layers size={13} /> : <Icon.help size={13} />}</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{obj.name || obj.label || "—"}</span>
            {isGroup ? <Pill tone="primary">{t('queues.qEventPickers.text2')}{obj.count ?? 0}</Pill> : <Pill tone="neutral">{t('queues.qEventPickers.text3')}</Pill>}
            <IconBtn size={32} onClick={() => rm(i)} title={t('queues.qEventPickers.title')}><Icon.x size={15} /></IconBtn>
          </div>
        );
      })}
      <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPicking(true)} testid="qpage-add-questions-btn">{t('queues.qEventPickers.text4')}</Btn></div>
      {picking ? (
        <QPickerModal title={t('queues.qEventPickers.title2')} onClose={() => setPicking(false)}>
          <div style={{ padding: "12px 16px 8px" }}>
            <Input full leading={<Icon.search size={14} />} size="sm" autoFocus placeholder={t('queues.qEventPickers.placeholder')} value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            {gs.map((g) => (
              <MediaRow key={"g" + g.id} onClick={() => add("group", g.id)}
                leading={<span style={{ width: 32, height: 32, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.layers size={15} /></span>}
                title={g.name} subtitle={`${t('queues.qEventPickers.text5')}${g.count} ${t('queues.qEventPickers.text6')}`} trailing={<Icon.plus size={16} />} style={{ borderRadius: 10 }} />
            ))}
            {qs.map((q) => (
              <MediaRow key={"q" + q.id} onClick={() => add("question", q.id)}
                leading={<span style={{ width: 32, height: 32, borderRadius: 7, background: T.n6, color: T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.help size={15} /></span>}
                title={q.label} subtitle={t('queues.qEventPickers.text3')} trailing={<Icon.plus size={16} />} style={{ borderRadius: 10 }} />
            ))}
            {gs.length + qs.length === 0 ? <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{query.trim() ? t('queues.qEventPickers.text8') : t('queues.qEventPickers.text9')}</div> : null}
          </div>
        </QPickerModal>
      ) : null}
    </div>
  );
}

// ── Survey (events EventSurveys UX, constrained to ONE survey per page) ──────
export function QSurveyPicker({ surveyId, surveys, onChange, title = "Survey", hint = "The survey shown when the customer taps the feedback button." }:
  { surveyId: string | null; surveys: QPickSurvey[]; onChange: (id: string | null) => void; title?: string; hint?: string }) {
  const t = useT();
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState("");
  const sel = surveyId ? surveys.find((s) => s.id === surveyId) : null;
  const options = surveys.filter((s) => s.id !== surveyId && (!query.trim() || s.name.toLowerCase().includes(query.toLowerCase())));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <PickHead title={title} hint={hint} />
      {!surveyId ? (
        <div style={{ padding: 14, background: T.n6, borderRadius: 8, fontSize: 12.5, color: T.soft }}>{t('queues.qEventPickers.text10')}</div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", border: `1px solid ${T.border}`, borderRadius: 9, background: T.white }}>
          <span style={{ width: 26, height: 26, borderRadius: 6, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.csv size={13} /></span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sel ? sel.name : surveyId}</span>
          {sel ? <Pill tone="neutral">{sel.count} {t('queues.qEventPickers.text6')}{" "}{sel.count === 1 ? "" : t('queues.qEventPickers.text7')}</Pill> : null}
          <IconBtn size={32} onClick={() => onChange(null)} title={t('queues.qEventPickers.title')}><Icon.x size={15} /></IconBtn>
        </div>
      )}
      <div><Btn variant="secondary" size="sm" icon={<Icon.plus size={13} />} onClick={() => setPicking(true)} testid="qpage-add-survey-btn">{surveyId ? t('queues.qEventPickers.text11') : t('queues.qEventPickers.text12')}</Btn></div>
      {picking ? (
        <QPickerModal title={t('queues.qEventPickers.title3')} onClose={() => setPicking(false)}>
          <div style={{ padding: "12px 16px 8px" }}>
            <Input full leading={<Icon.search size={14} />} size="sm" autoFocus placeholder={t('queues.qEventPickers.placeholder2')} value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            {options.map((s) => (
              <MediaRow key={s.id} onClick={() => { onChange(s.id); setPicking(false); }} leading={<span style={{ width: 32, height: 32, borderRadius: 7, background: T.p4, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.csv size={15} /></span>}
                title={<>{s.name}</>} subtitle={<>{s.count} {t('queues.qEventPickers.text6')}{" "}{s.count === 1 ? "" : t('queues.qEventPickers.text7')}{" "}{s.description ? /* TODO(i18n): replace template-literal with t('queues.qEventPickers.text13', { ... }) */` · ${s.description}` : ""}</>}
                trailing={<><Icon.plus size={16} /></>} />

            ))}
            {options.length === 0 ? <div style={{ padding: 24, textAlign: "center", color: T.soft, fontSize: 13 }}>{query.trim() ? t('queues.qEventPickers.text14') : surveys.length ? t('queues.qEventPickers.text15') : t('queues.qEventPickers.text16')}</div> : null}
          </div>
        </QPickerModal>
      ) : null}
    </div>
  );
}
